import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bell, ChevronDown, LogOut, Moon, Sun, User } from 'lucide-react'

import { Avatar } from '@/components/common/Avatar'
import { WsStatusBadge } from '@/components/WsStatusBadge'
import { useNotifications } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'
import { useBrandingStore } from '@/stores/brandingStore'
import { useUIStore } from '@/stores/uiStore'
import { useWebSocketStore } from '@/stores/websocketStore'
import { cn } from '@/utils/cn'

const pageTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/instances': 'Instancias',
  '/chat': 'Chat',
  '/users': 'Usuarios',
  '/settings': 'Configuracoes',
}

export function Header() {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notificationsRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const { theme, toggleTheme } = useUIStore()
  const { user, logout } = useAuth()
  const platformName = useBrandingStore((state) => state.platform_name)
  const wsConnectionState = useWebSocketStore((state) => state.connectionState)
  const wsReconnectAttempt = useWebSocketStore((state) => state.reconnectAttempt)
  const {
    count,
    items,
    loadingItems,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications()

  const pageTitle = pageTitles[location.pathname] ?? platformName ?? 'ISing'

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotifications(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function formatNotificationTime(value: string) {
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    } catch {
      return ''
    }
  }

  return (
    <header className="glass-header h-[var(--header-height)] flex items-center justify-between px-7 shrink-0 z-10">
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
        {pageTitle}
      </h1>

      <div className="flex items-center gap-2.5">
        {location.pathname === '/chat' ? (
          <WsStatusBadge state={wsConnectionState} attempt={wsReconnectAttempt} compact />
        ) : null}

        <div ref={notificationsRef} className="relative">
          <button
            type="button"
            onClick={() => {
              const nextOpen = !showNotifications
              setShowNotifications(nextOpen)
              if (nextOpen) {
                fetchNotifications().catch(() => undefined)
              }
            }}
            className={cn(
              'relative w-10 h-10 rounded-[var(--radius-md)] flex items-center justify-center',
              'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              'hover:bg-[var(--glass-medium)] transition-colors duration-150',
            )}
            aria-label={
              count > 0
                ? `${count} notificacoes nao lidas`
                : 'Nenhuma notificacao nao lida'
            }
          >
            <Bell size={20} />
            {count > 0 ? <span className="notif-badge">{count > 99 ? '99+' : count}</span> : null}
          </button>

          {showNotifications ? (
            <div className="absolute right-0 top-full mt-2 w-[360px] glass-dropdown z-50 overflow-hidden animate-slideDown">
              <div className="flex items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">Notificacoes</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {count > 0 ? `${count} nao lida(s)` : 'Tudo em dia'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => markAllAsRead().catch(() => undefined)}
                  disabled={count === 0}
                  className="text-xs font-medium text-[var(--color-brand)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Marcar todas
                </button>
              </div>

              <div className="max-h-[420px] overflow-y-auto px-2 py-2">
                {loadingItems ? (
                  <div className="px-3 py-8 text-center text-sm text-[var(--text-muted)]">
                    Carregando notificacoes...
                  </div>
                ) : error ? (
                  <div className="space-y-3 px-3 py-6 text-center">
                    <p className="text-sm text-[var(--color-error)]">{error}</p>
                    <button
                      type="button"
                      onClick={() => fetchNotifications().catch(() => undefined)}
                      className="text-sm font-medium text-[var(--color-brand)]"
                    >
                      Tentar novamente
                    </button>
                  </div>
                ) : items.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-[var(--text-muted)]">
                    Nenhuma notificacao encontrada.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((notification) => {
                      const unread = !notification.read_at
                      return (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => {
                            if (unread) {
                              markAsRead(notification.id).catch(() => undefined)
                            }
                          }}
                          className={cn(
                            'w-full rounded-[16px] border px-3 py-3 text-left transition',
                            unread
                              ? 'border-[rgba(var(--color-brand-rgb),0.2)] bg-[rgba(var(--color-brand-rgb),0.1)]'
                              : 'border-[var(--border-subtle)] bg-[var(--glass-light)]',
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-[var(--text-primary)]">
                                {notification.title}
                              </p>
                              {notification.description ? (
                                <p className="text-xs leading-5 text-[var(--text-secondary)]">
                                  {notification.description}
                                </p>
                              ) : null}
                            </div>
                            {unread ? (
                              <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[var(--color-brand)]" />
                            ) : null}
                          </div>
                          <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                            {formatNotificationTime(notification.created_at)}
                          </p>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <button
          onClick={toggleTheme}
          className={cn(
            'w-10 h-10 rounded-[var(--radius-md)] flex items-center justify-center',
            'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            'hover:bg-[var(--glass-medium)] transition-colors duration-150',
          )}
          aria-label={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setShowUserMenu((value) => !value)}
            className={cn(
              'flex items-center gap-2 px-2 py-1.5 rounded-[var(--radius-md)]',
              'hover:bg-[var(--glass-medium)] transition-colors duration-150',
            )}
            aria-expanded={showUserMenu}
            aria-haspopup="true"
          >
            <Avatar name={user?.full_name ?? 'U'} src={user?.avatar_url} size="sm" />
            <span className="text-sm font-medium text-[var(--text-primary)] hidden sm:block max-w-[120px] truncate">
              {user?.full_name}
            </span>
            <ChevronDown
              size={16}
              className={cn(
                'text-[var(--text-muted)] transition-transform duration-150',
                showUserMenu && 'rotate-180',
              )}
            />
          </button>

          {showUserMenu ? (
            <div className="absolute right-0 top-full mt-2 w-48 glass-dropdown z-50 overflow-hidden animate-slideDown">
              <div className="p-1">
                <button
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-[var(--radius-sm)]',
                    'text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                    'hover:bg-[var(--glass-medium)] transition-colors duration-150',
                  )}
                  onClick={() => setShowUserMenu(false)}
                >
                  <User size={16} />
                  Meu perfil
                </button>
                <div className="h-px bg-[var(--border-subtle)] my-1" />
                <button
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-[var(--radius-sm)]',
                    'text-sm text-[var(--color-error)]',
                    'hover:bg-[rgba(239,68,68,0.1)] transition-colors duration-150',
                  )}
                  onClick={() => {
                    setShowUserMenu(false)
                    logout()
                  }}
                >
                  <LogOut size={16} />
                  Sair
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}
